import { useRef, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { describeMemoryWriteError, forgetSolMemory } from '../../data/writers/sol-memory-writers';
import type { SolMemoryCard } from '../../data/sol/sol-contract';
import {
  MEMORY_FORGET_ACTION,
  MEMORY_FORGOTTEN_CHIP_TEXT,
  MEMORY_KIND_LABEL,
  MEMORY_NOTED_LABEL,
} from './sol-wording';

/**
 * THE VISIBLE MEMORY — what Sol remembered, with Forget right beside it.
 * M11 / D-040.
 *
 * **NOTHING AUTOMATIC IS EVER SILENT.** When Sol notes a preference from
 * something Brandon said, this chip appears under the answer naming exactly
 * what was kept, and Forget removes it in one tap through the memory writer.
 * The same chip acknowledges an explicit "remember" — the Undo there is the
 * same Forget.
 *
 * **FORGET IS A MEMORY WRITE, NOT AN OPERATIONAL ONE.** It reaches
 * `forget_sol_memory` and nothing else; no Capture, Project, status or report
 * is in its path, and no Confirm card is involved — he is undoing his own
 * memory, not changing saved site information.
 */

export interface SolMemoryNotedCardProps {
  readonly memory: SolMemoryCard;
  /** `noted` — Sol kept it on its own. `saved` — he asked. Same control. */
  readonly reason: 'noted' | 'saved';
  readonly disabled?: boolean | undefined;
}

type ChipState =
  | { readonly kind: 'kept' }
  | { readonly kind: 'forgetting' }
  | { readonly kind: 'forgotten' }
  | { readonly kind: 'failed'; readonly text: string };

export function SolMemoryNotedCard({ memory, reason, disabled }: SolMemoryNotedCardProps) {
  const [state, setState] = useState<ChipState>({ kind: 'kept' });
  const inFlight = useRef(false);

  const forget = (): void => {
    if (inFlight.current || state.kind === 'forgotten' || disabled === true) return;
    const client = getSupabaseClient();
    if (!client) {
      setState({ kind: 'failed', text: 'Crewlink is not connected to a server yet.' });
      return;
    }
    inFlight.current = true;
    setState({ kind: 'forgetting' });
    void forgetSolMemory(client, memory.id)
      .then(() => {
        setState({ kind: 'forgotten' });
      })
      .catch((caught: unknown) => {
        const message = caught instanceof Error ? caught.message : String(caught);
        setState({ kind: 'failed', text: describeMemoryWriteError(message) });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  return (
    <div className="sol-memory-chip" role="status">
      <span className="sol-memory-chip-label">
        {reason === 'noted' ? MEMORY_NOTED_LABEL : 'Remembered'} ·{' '}
        {MEMORY_KIND_LABEL[memory.kind].toLowerCase()}
      </span>
      <span className="sol-memory-chip-text">{memory.content}</span>
      {state.kind === 'forgotten' ? (
        <span className="sol-memory-chip-done">{MEMORY_FORGOTTEN_CHIP_TEXT}</span>
      ) : (
        <button
          type="button"
          className="sol-memory-chip-forget"
          onClick={forget}
          disabled={state.kind === 'forgetting' || disabled === true}
        >
          {state.kind === 'forgetting' ? 'Forgetting…' : MEMORY_FORGET_ACTION}
        </button>
      )}
      {state.kind === 'failed' ? (
        <span className="sol-memory-chip-failure" role="alert">
          {state.text}
        </span>
      ) : null}
    </div>
  );
}
